import { EditorChangeType } from '@src/editor/types';
import { useEffect, useState } from 'react';

import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { Renderer } from '@src/renderer/Renderer';
import type { RendererState } from '@src/renderer/RendererState';

export interface UseRendererPlaybackParams {
  /** Live Creatomate renderer. `null` until `useClipPreviewRenderer` initialises it. */
  renderer: Renderer | null;
  /** Editor store, for timeline-driven duration changes. */
  editorStore: ClipPreviewEditorStore | null;
  /** Gate: subscriptions only attach once the renderer is ready. */
  isReady: boolean;
  /** Callbacks from `usePlaybackState` to drive state updates. */
  callbacks: {
    onTimeChange: (time: number) => void;
    onPlay: () => void;
    onPause: () => void;
    onDurationChange: (duration: number) => void;
  };
}

/**
 * Wires a Creatomate `Renderer` (and its associated `ClipPreviewEditorStore`)
 * to the source-agnostic `usePlaybackState` callbacks. Handles:
 *
 * - Chain-wrapping `renderer.onTimeChange` / `onPlay` / `onPause` so existing
 *   adapter/timeRef bookkeeping continues to fire.
 * - Detecting source swaps via `renderer.onStateChange` and re-attaching
 *   handlers (callbackVersion bump).
 * - Observing `TIMELINE_CHANGED` from the editor for duration updates.
 * - Unmount cleanup of all patched handler slots.
 */
export function useRendererPlayback({
  renderer,
  editorStore,
  isReady,
  callbacks,
}: UseRendererPlaybackParams): void {
  const { onTimeChange, onPlay, onPause, onDurationChange } = callbacks;

  // `callbackVersion` forces the time/play/pause subscription to re-run when
  // the renderer's source swaps (e.g. aspect-ratio change).
  const [callbackVersion, setCallbackVersion] = useState(0);

  // Source / state changes → duration update + callbackVersion bump.
  useEffect(() => {
    if (!renderer || !isReady) return;

    const existingOnStateChange = renderer.onStateChange;

    const handleSourceChange = (rendererState: RendererState) => {
      setCallbackVersion(v => v + 1);
      if (rendererState?.source?.duration) {
        onDurationChange(rendererState.source.duration);
      }
    };

    const newOnStateChange = (rendererState: RendererState) => {
      if (existingOnStateChange && existingOnStateChange !== newOnStateChange) {
        existingOnStateChange(rendererState);
      }
      handleSourceChange(rendererState);
    };
    renderer.onStateChange = newOnStateChange;

    const source = renderer.getSource();
    if (source?.duration) {
      onDurationChange(source.duration);
    }

    return () => {
      if (renderer.onStateChange === newOnStateChange) {
        renderer.onStateChange = existingOnStateChange;
      }
    };
  }, [isReady, renderer, onDurationChange]);

  // Time / play / pause subscriptions — chain-wrap the existing handlers
  // (`useClipPreviewRenderer` already set them for adapter/timeRef bookkeeping).
  useEffect(() => {
    if (!renderer || !isReady) return;

    const existingOnTimeChange = renderer.onTimeChange;
    const existingOnPlay = renderer.onPlay;
    const existingOnPause = renderer.onPause;

    const newOnTimeChange = (time: number) => {
      if (existingOnTimeChange && existingOnTimeChange !== newOnTimeChange) {
        existingOnTimeChange(time);
      }
      onTimeChange(time);
    };
    renderer.onTimeChange = newOnTimeChange;

    const newOnPlay: () => void = () => {
      if (existingOnPlay && existingOnPlay !== newOnPlay) {
        existingOnPlay();
      }
      onPlay();
    };
    renderer.onPlay = newOnPlay;

    const newOnPause: () => void = () => {
      if (existingOnPause && existingOnPause !== newOnPause) {
        existingOnPause();
      }
      onPause();
    };
    renderer.onPause = newOnPause;

    return () => {
      if (renderer.onTimeChange === newOnTimeChange) {
        renderer.onTimeChange = existingOnTimeChange;
      }
      if (renderer.onPlay === newOnPlay) {
        renderer.onPlay = existingOnPlay;
      }
      if (renderer.onPause === newOnPause) {
        renderer.onPause = existingOnPause;
      }
    };
  }, [isReady, renderer, callbackVersion, onTimeChange, onPlay, onPause]);

  // Editor observer — timeline-driven duration changes (e.g. trim edits).
  useEffect(() => {
    if (!editorStore) return;

    const editorObserver = {
      onEditorChange: (editor: VideoEditor, changeType: EditorChangeType) => {
        if (changeType === EditorChangeType.TIMELINE_CHANGED) {
          onDurationChange(editor.timeline.duration);
        }
      },
    };

    editorStore.editor.registerObserver(editorObserver);
    return () => {
      editorStore.editor.unregisterObserver(editorObserver);
    };
  }, [editorStore, onDurationChange]);
}
